/**
 * DelegationFormDialog — J7-W3 task 3a (PRD §12.3.10) / Sheet Migration Wave 5
 *
 * Create form for a delegation. Reuses CardSelector for the scope picker,
 * MultiSelect for domains, DateInput for the time window, and the existing
 * MultiSelect for the optional permissions key list.
 *
 * Sheet Migration W5: converted from Modal to FormSheet with useFormSnapshot.
 * Create-only — no URL routing.
 *
 * Validation:
 *   - delegator and delegate are required and must differ
 *   - expires_at must be > starts_at (Zod also checks; UI guards early)
 *   - scope='domain' requires ≥ 1 domain_id (matches Zod refinement)
 *   - max 90-day duration (matches Zod refinement)
 *
 * GOV-06 / GOV-04 errors from the server surface inline; the message text
 * comes back in `err.message` (AuthorizationError → 403) and is displayed
 * verbatim so the user can see exactly which rule fired.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { t } from '@/lib/i18n';
import { FormSheet } from '@/components/ui/FormSheet';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { Alert } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { CardSelector } from '@/components/ui/CardSelector';
import { MultiSelect } from '@/components/ui/MultiSelect/MultiSelect';
import { DateInput } from '@/components/ui/DateInput/DateInput';
import { useRorosActors } from '@/hooks/roros/actors';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useRorosDelegationCreate } from '@/hooks/roros/delegations';
import type { RorosActorDTO, CreateDelegationPayload } from '@/types/roros/actors';
import type { RorosDomainDTO } from '@/types/roros/domains';

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

type DelegationSnapshot = {
  delegatorActorId: string;
  delegateActorId: string;
  scope: 'full' | 'domain' | 'custom';
  domainIds: string[];
  permissionKeys: string[];
  startsAt: string; // ISO YYYY-MM-DD
  expiresAt: string; // ISO YYYY-MM-DD
  decisionNoteId: string;
};

function diffDelegationSnapshots(
  base: DelegationSnapshot,
  cur: DelegationSnapshot,
): Partial<Record<keyof DelegationSnapshot, boolean>> {
  return {
    delegatorActorId: base.delegatorActorId !== cur.delegatorActorId,
    delegateActorId: base.delegateActorId !== cur.delegateActorId,
    scope: base.scope !== cur.scope,
    domainIds: JSON.stringify(base.domainIds) !== JSON.stringify(cur.domainIds),
    permissionKeys: JSON.stringify(base.permissionKeys) !== JSON.stringify(cur.permissionKeys),
    startsAt: base.startsAt !== cur.startsAt,
    expiresAt: base.expiresAt !== cur.expiresAt,
    decisionNoteId: base.decisionNoteId !== cur.decisionNoteId,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function plusDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

interface FieldErrors {
  delegatorActorId?: string;
  delegateActorId?: string;
  scope?: string;
  domainIds?: string[] | string;
  startsAt?: string;
  expiresAt?: string;
  general?: string;
}

const DEFAULT_PERMISSION_KEYS = [
  'approve_decisions',
  'sign_off_quality',
  'edit_outcomes',
  'manage_routines',
  'budget_authority',
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export function DelegationFormDialog({ isOpen, onClose, onCreated }: Props) {
  const createMutation = useRorosDelegationCreate();
  const actorsQuery = useRorosActors();
  const domainsQuery = useRorosDomains();

  const [errors, setErrors] = useState<FieldErrors>({});

  // ─── Form state via useFormSnapshot ──────────────────────────────────────
  // Create-only dialog: depKey stays null; isOpen transition re-seeds the form.
  const { state, setField, baseline, current } = useFormSnapshot<DelegationSnapshot>({
    initialFactory: () => ({
      delegatorActorId: '',
      delegateActorId: '',
      scope: 'full',
      domainIds: [],
      permissionKeys: [],
      startsAt: todayIso(),
      expiresAt: plusDaysIso(30),
      decisionNoteId: '',
    }),
    isOpen,
    depKey: null,
  });

  // Reset validation errors on open.
  useEffect(() => {
    if (isOpen) setErrors({});
  }, [isOpen]);

  // ─── Select options ───────────────────────────────────────────────────────

  const actorOptions = useMemo(() => {
    return ((actorsQuery.data as RorosActorDTO[] | undefined) ?? [])
      .filter((a) => a.status === 'active')
      .map((a) => ({ value: a.id, label: `${a.name} (${a.actorType})` }));
  }, [actorsQuery.data]);

  const domainOptions = useMemo(() => {
    return ((domainsQuery.data as RorosDomainDTO[] | undefined) ?? [])
      .filter((d) => d.isActive)
      .map((d) => ({ value: d.id, label: d.name }));
  }, [domainsQuery.data]);

  const permissionOptions = useMemo(
    () => DEFAULT_PERMISSION_KEYS.map((k) => ({ value: k, label: k.replace(/_/g, ' ') })),
    [],
  );

  // ─── Validation ───────────────────────────────────────────────────────────

  const validate = (): FieldErrors => {
    const e: FieldErrors = {};
    if (!state.delegatorActorId) e.delegatorActorId = t('Delegator is required');
    if (!state.delegateActorId) e.delegateActorId = t('Delegate is required');
    if (state.delegatorActorId && state.delegatorActorId === state.delegateActorId) {
      e.delegateActorId = t('Delegate must differ from delegator');
    }
    if (state.scope === 'domain' && state.domainIds.length === 0) {
      e.domainIds = t('Pick at least one domain for a domain-scoped delegation');
    }
    if (!state.startsAt) e.startsAt = t('Start date is required');
    if (!state.expiresAt) e.expiresAt = t('Expiry date is required');
    if (state.startsAt && state.expiresAt && new Date(state.expiresAt) <= new Date(state.startsAt)) {
      e.expiresAt = t('Expiry must be after start date');
    }
    if (state.startsAt && state.expiresAt) {
      const ms = new Date(state.expiresAt).getTime() - new Date(state.startsAt).getTime();
      if (ms > 90 * 24 * 60 * 60 * 1000) {
        e.expiresAt = t('Delegation duration cannot exceed 90 days');
      }
    }
    return e;
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = () => {
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    // Convert ISO date (YYYY-MM-DD) to ISO datetime (start of day UTC) — the
    // server's Zod schema requires .datetime() format.
    const startsAtIso = `${state.startsAt}T00:00:00.000Z`;
    const expiresAtIso = `${state.expiresAt}T23:59:59.000Z`;

    // Permissions JSONB shape: simple `{ key: true }` map. The server schema
    // accepts any record; we keep the shape predictable so the detail view
    // can render it as a list.
    const permissions: Record<string, boolean> = {};
    for (const key of state.permissionKeys) permissions[key] = true;

    const payload: CreateDelegationPayload = {
      delegator_actor_id: state.delegatorActorId,
      delegate_actor_id: state.delegateActorId,
      scope: state.scope,
      domain_ids: state.scope === 'domain' || state.scope === 'custom' ? state.domainIds : [],
      permissions,
      starts_at: startsAtIso,
      expires_at: expiresAtIso,
      decision_note_id: state.decisionNoteId.trim() || undefined,
    };

    createMutation.mutate(payload, {
      onSuccess: () => {
        onCreated?.();
        onClose();
      },
      onError: (err: any) => {
        setErrors({
          general: err?.message ?? t('Failed to create delegation'),
        });
      },
    });
  };

  return (
    <FormSheet<DelegationSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={t('New Delegation')}
      mode="create"
      baseline={baseline}
      current={current}
      diff={diffDelegationSnapshots}
      onSubmit={handleSubmit}
      isSubmitting={createMutation.isPending}
      submitLabel={t('Create Delegation')}
      submittingLabel={t('Creating…')}
      submitTestId="delegation-submit-button"
      width="md"
    >
      <div className="space-y-5 pt-2" data-testid="delegation-form">
        {errors.general && (
          <Alert variant="error" title={t('Cannot create delegation')}>
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden />
              <span data-testid="delegation-form-error">{errors.general}</span>
            </div>
          </Alert>
        )}

        {/* Delegator — Radix Select rejects empty-string values, so we leave
            value undefined when nothing is picked and rely on the trigger's
            placeholder for the "select an actor" prompt. */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Delegator')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <Select
            value={state.delegatorActorId || undefined}
            placeholder={t('Select an actor…')}
            onValueChange={(v) => {
              setField('delegatorActorId', v);
              setErrors((p) => ({ ...p, delegatorActorId: undefined }));
            }}
            options={actorOptions}
            error={errors.delegatorActorId}
            data-testid="delegation-delegator-select"
          />
        </div>

        {/* Delegate */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Delegate')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <Select
            value={state.delegateActorId || undefined}
            placeholder={t('Select an actor…')}
            onValueChange={(v) => {
              setField('delegateActorId', v);
              setErrors((p) => ({ ...p, delegateActorId: undefined }));
            }}
            options={actorOptions}
            error={errors.delegateActorId}
            data-testid="delegation-delegate-select"
          />
        </div>

        {/* Scope */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('Scope')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <CardSelector
            mode="single"
            selected={state.scope}
            onChange={(v) => setField('scope', v as 'full' | 'domain' | 'custom')}
            items={[
              { id: 'full', title: t('Full'), description: t('Hand over every right the delegator holds.') },
              { id: 'domain', title: t('Domain'), description: t('Restrict the delegation to one or more domains.') },
              { id: 'custom', title: t('Custom'), description: t('Pick exact permissions and (optionally) domains.') },
            ]}
            data-testid="delegation-scope-selector"
          />
        </div>

        {/* Domains — visible when scope=domain or custom */}
        {(state.scope === 'domain' || state.scope === 'custom') && (
          <div data-testid="delegation-domains-section">
            <MultiSelect
              label={
                state.scope === 'domain'
                  ? t('Domains (required for domain scope)')
                  : t('Domains (optional)')
              }
              value={state.domainIds}
              onChange={(v) => {
                setField('domainIds', v);
                setErrors((p) => ({ ...p, domainIds: undefined }));
              }}
              options={domainOptions}
              placeholder={t('Pick domains…')}
              error={typeof errors.domainIds === 'string' ? errors.domainIds : undefined}
            />
          </div>
        )}

        {/* Permissions (custom scope only) */}
        {state.scope === 'custom' && (
          <div data-testid="delegation-permissions-section">
            <MultiSelect
              label={t('Permissions')}
              value={state.permissionKeys}
              onChange={(v) => setField('permissionKeys', v)}
              options={permissionOptions}
              placeholder={t('Pick permissions…')}
            />
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('Starts on')} <span className="text-error-500" aria-hidden>*</span>
            </label>
            <DateInput
              value={state.startsAt}
              onChange={(iso) => {
                setField('startsAt', iso);
                setErrors((p) => ({ ...p, startsAt: undefined }));
              }}
              error={errors.startsAt}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('Expires on')} <span className="text-error-500" aria-hidden>*</span>
            </label>
            <DateInput
              value={state.expiresAt}
              onChange={(iso) => {
                setField('expiresAt', iso);
                setErrors((p) => ({ ...p, expiresAt: undefined }));
              }}
              error={errors.expiresAt}
            />
          </div>
        </div>

        {/* Decision Note (optional) */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Linked Decision Note (optional)')}
          </label>
          <Input
            type="text"
            value={state.decisionNoteId}
            onChange={(e) => setField('decisionNoteId', e.target.value)}
            placeholder={t('Decision Note id (UUID)')}
            data-testid="delegation-decision-note-input"
          />
        </div>
      </div>
    </FormSheet>
  );
}
